// Panel de la herramienta "Imágenes": tabs Generadas / Subidas + Subir archivo.
// Reusa la capa de datos existente (fetchHistorialImages, brandAssets). Al clickear
// una imagen se agrega como capa. Las subidas quedan en brand_assets (tipo 'upload'),
// reusables entre imágenes. Todo front-only.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { fetchHistorialImages } from '../../v2/productsApi';
import {
  fetchBrandAssets,
  getBrandAssetSignedUrl,
  uploadBrandAsset,
} from '../../../utils/brandAssets';
import { loadImageRatio, makeImage } from '../lib/factory';
import { PanelSheet } from './PanelSheet';
import type { SceneApi } from '../scene/useScene';

type Tab = 'generadas' | 'subidas';
interface Item {
  key: string;
  url: string;
  ratio?: number;
}

interface Props {
  api: SceneApi;
  companyId: string;
  brandId: string | null;
  onClose: () => void;
}

export function ImagenesPanel({ api, companyId, brandId, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('generadas');
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!brandId) {
        setItems([]);
        return;
      }
      setLoading(true);
      let next: Item[] = [];
      if (tab === 'generadas') {
        const rows = await fetchHistorialImages(companyId, brandId);
        next = rows.filter((r) => r.imageUrl).map((r) => ({ key: r.id, url: r.imageUrl }));
      } else {
        const assets = await fetchBrandAssets(brandId, 'upload');
        const withUrls = await Promise.all(
          assets.map(async (a) => {
            const url = await getBrandAssetSignedUrl(a.storage_path);
            return url
              ? { key: a.id, url, ratio: a.width && a.height ? a.width / a.height : undefined }
              : null;
          }),
        );
        next = withUrls.filter(Boolean) as Item[];
      }
      if (!cancelled) {
        setItems(next);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, brandId, companyId]);

  const addImage = async (url: string, ratio?: number) => {
    const r = ratio ?? (await loadImageRatio(url));
    api.addLayer(makeImage(url, api.scene.ratio, r));
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !brandId) return;
    setUploading(true);
    try {
      const asset = await uploadBrandAsset({ file, brandProfileId: brandId, companyId, tipo: 'upload' });
      const url = await getBrandAssetSignedUrl(asset.storage_path);
      if (url) {
        await addImage(url, asset.width && asset.height ? asset.width / asset.height : undefined);
        setTab('subidas');
      }
    } catch (err) {
      console.error('Edición gráfica: error subiendo imagen', err);
    } finally {
      setUploading(false);
    }
  };

  const tabBtn = (t: Tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(t)}
      className={`flex-1 h-8 rounded-md text-[13px] font-medium transition ${
        tab === t ? 'bg-brand-primary text-white' : 'text-neutral-600 hover:bg-neutral-100'
      }`}
    >
      {label}
    </button>
  );

  return (
    <PanelSheet title="Imágenes" onClose={onClose}>
      <div className="p-3 pb-2 shrink-0">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={!brandId || uploading}
          className="w-full h-10 rounded-md bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[14px] font-medium shadow-sm inline-flex items-center justify-center gap-2 active:scale-[0.99] transition-colors"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Subir archivo
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
      </div>

      {/* `pb-2` = gap blanco FIJO entre las tabs y la grilla: como la grilla
          scrollea, su padding-top se va al deslizar y las imágenes tocaban las
          tabs. Este espacio vive fuera del scroll, así siempre queda aire. */}
      <div className="px-3 pb-2 flex gap-1 shrink-0">
        {tabBtn('generadas', 'Generadas')}
        {tabBtn('subidas', 'Subidas')}
      </div>

      {/* El padding va en el CONTENIDO, no en el scroll-container: Chrome ignora
          el padding-bottom de un contenedor con overflow, así que la última fila
          quedaba pegada al borde al scrollear. En el hijo sí se respeta → queda
          un margen blanco parejo alrededor de la grilla. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-3 pb-3 pt-1">
          {!brandId ? (
            <p className="text-[13px] text-neutral-400 text-center mt-6 px-2">
              Necesitás una marca activa para ver tus imágenes.
            </p>
          ) : loading ? (
            <div className="flex justify-center mt-8">
              <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-[13px] text-neutral-400 text-center mt-6 px-2">
              {tab === 'generadas' ? 'No hay imágenes generadas.' : 'Todavía no subiste imágenes.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => addImage(it.url, it.ratio)}
                  className="aspect-square rounded-md overflow-hidden border border-neutral-200 hover:border-brand-primary/50 active:scale-[0.98] transition"
                >
                  <img src={it.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </PanelSheet>
  );
}
